import { Grid } from "./Grid";
import { IGridConfig, ICol, GridEvents, GridSystemEvents, IRow, IProGrid, TRowStatus } from "./types";
import { DataEvents, dragManager, IDataCollection, IDataItem, isTreeCollection } from "@dhx/ts-data";
import { locateNodeByClassName, toNode } from "@dhx/ts-common/html";
import { getCalculatedRowHeight, getMaxRowHeight } from "./helpers/data";
import { getRenderConfig, proRender } from "./ui/render";
import { create } from "@dhx/ts-common/dom";
import { ScrollView } from "@dhx/ts-common/ScrollView";

export class ProGrid extends Grid implements IProGrid {
	public scrollView: ScrollView;

	constructor(container: HTMLElement | string, config?: IGridConfig) {
		super(container, {
			autoHeight: false,
			...config,
		});
		this.scrollView = new ScrollView(() => this.getRootView(), {
			scrollHandler: e =>
				this.events.fire(GridEvents.scroll, [
					{
						y: e.target.scrollTop,
						x: e.target.scrollLeft,
					},
				]),
		});
	}

	protected _createView() {
		return create(
			{
				render: (vm, obj) => {
					return proRender(vm, obj, this._htmlEvents, this.selection, this._uid);
				},
			},
			this
		);
	}

	protected _setEventHandlers() {
		super._setEventHandlers();

		this.events.on(GridEvents.headerCellMouseDown, (col: ICol, e: TouchEvent & MouseEvent) => {
			const targetRow = locateNodeByClassName(e, "dhx_header-row");
			const targetRowIndex = targetRow && targetRow.getAttribute("aria-rowindex");
			if (col.header[Number(targetRowIndex) - 1]?.content) return;
			if (!e.targetTouches) {
				this._dragStartColumn(e, col);
			} else {
				this._touch.timer = setTimeout(() => {
					this._dragStartColumn(e, col);
				}, this._touch.duration);
			}
		});

		this._events.on(GridSystemEvents.headerCellTouchMove, (_col: ICol, e: TouchEvent) => {
			this._touch.start && e.preventDefault();
			this._clearTouchTimer();
		});

		this._events.on(GridSystemEvents.headerCellTouchEnd, () => {
			this._touch.start = false;
			this._clearTouchTimer();
		});

		this.events.on(GridEvents.resize, () => {
			this._parseColumns();
			this._checkFilters();
		});

		this.events.on(GridEvents.afterResizeEnd, () => {
			if (this.config.autoHeight) {
				this.config.data = this.data.map(row => {
					const height = getMaxRowHeight(row, this.config.columns);
					row.$height = getCalculatedRowHeight(height, {
						rowHeight: this.config.rowHeight,
						padding: 8,
					});
					return row;
				});
			}
		});

		this.events.on(GridEvents.afterRowResize, (row: IRow, _e: Event, currentHeight: number) => {
			const { id, height, $height } = this.data.getItem(row.id as string);
			if (height && height !== $height) {
				this.data.update(id, { height: currentHeight });
			}
			this.data.update(id, { $height: currentHeight }, true);

			this.config.data = this.data.map(row => row);
			this.paint();
		});

		this.events.on(GridEvents.scroll, () => {
			this._lazyLoad();
		});

		this.events.on(DataEvents.dataRequest, (from, to) => {
			const proxy = this.data.dataProxy;
			if (proxy && proxy.config) {
				this._prepareDataFromTo(this.data, from, to);
			}
		});
	}

	protected _prepareData(data: IDataItem[] | IDataCollection) {
		this._adjustColumns();

		let convertedData;
		if (Array.isArray(data) || isTreeCollection(data)) {
			convertedData = data;
		} else {
			const initData = data.getInitialData() || [];
			convertedData = initData.length !== 0 ? initData : data.getRawData(0, data.getLength());
		}

		return convertedData.map((row: IRow) => {
			if (this.config.autoHeight && typeof row.height === "undefined") {
				const height = getMaxRowHeight(row, this.config.columns);
				row.$height =
					getCalculatedRowHeight(height, {
						rowHeight: this.config.rowHeight,
						padding: 8,
					}) || this.config.rowHeight;
			} else {
				row.$height = row.height || this.config.rowHeight;
			}
			return row;
		});
	}

	protected _prepareDataFromTo(data: IDataCollection, from: number, to: number) {
		return data.mapRange(from, to, (row: IRow) => {
			const height = getMaxRowHeight(row, this.config.columns);
			row.$height =
				getCalculatedRowHeight(height, {
					rowHeight: this.config.rowHeight,
					padding: 8,
				}) || this.config.rowHeight;
			return row;
		});
	}

	private _lazyLoad(): void {
		const proxy = this.data.dataProxy;

		if (proxy && proxy.config) {
			const data = this.data.getRawData(0, -1, null, 2);
			const renderConfig = getRenderConfig(this, data, {
				width: this.config.width,
				height: this.config.height,
			});
			let from: number;
			const initialLimit: number = proxy.config.limit;
			let limit: number = initialLimit;
			const currentRows: IRow[] = renderConfig.currentRows;
			const firstRow: IRow = currentRows[0];
			const lastRow: IRow = currentRows[currentRows.length - 1];
			const to: number = this.data.getIndex(lastRow.id);

			const getRowIndex = (row: TRowStatus) => {
				for (let i = 1; i < currentRows.length; i++) {
					if (
						(row === "firstFilledRow" && !currentRows[i].$empty && currentRows[i - 1].$empty) ||
						(row === "firstEmptyRow" && currentRows[i].$empty && !currentRows[i - 1].$empty)
					) {
						return this.data.getIndex(currentRows[i].id);
					}
				}
			};

			if (firstRow.$empty) {
				if (lastRow.$empty) {
					from = this.data.getIndex(firstRow.id);
				} else {
					const firstFilledRowIndex = getRowIndex("firstFilledRow");
					let start = firstFilledRowIndex - limit;
					if (start < 0) start = 0;

					for (let i = start; i < firstFilledRowIndex; i++) {
						if (this.data.getItem(this.data.getId(i)).$empty) {
							from = i;
							limit = firstFilledRowIndex - i;
							break;
						}
					}
				}
			} else if (lastRow.$empty) {
				from = getRowIndex("firstEmptyRow");
			} else {
				from = getRowIndex("firstEmptyRow") ?? this.data.getIndex(firstRow.id);
				limit = (getRowIndex("firstFilledRow") ?? to) - from;
			}

			if (!this.data.isDataLoaded(from, to) && this.data.events.fire(DataEvents.beforeLazyLoad, [])) {
				proxy.updateUrl(null, { from, limit });
				proxy.config.limit = initialLimit;
				this.data.load(proxy).then(() => {
					this.config.autoHeight && this._prepareDataFromTo(this.data, from, to);
				});
			}
		}
	}

	private _getColumnGhost(col: ICol) {
		const container = this._container || toNode(this._uid);
		const headerRow = container.querySelector(".dhx_header-row");
		const colHeaderCell = headerRow.querySelector(`.dhx_grid-header-cell[data-dhx-id="${col.id}"]`);
		const headerCols = Array.from(headerRow.childNodes);
		const n = headerCols.indexOf(colHeaderCell) + 1;

		const colHeaderCells = container.querySelectorAll(
			`.dhx_grid-header-cell[data-dhx-id="${col.id}"]:not(.dhx_span-cell)`
		);

		const ghostGridNodes: NodeList = container.querySelectorAll(
			`.dhx_grid_data .dhx_grid-cell:nth-child(${n})`
		);
		const ghostContainer = document.createElement("div");
		colHeaderCells.forEach(node => ghostContainer.appendChild(node.cloneNode(true)));
		ghostGridNodes.forEach(node => ghostContainer.appendChild(node.cloneNode(true)));
		return ghostContainer;
	}

	private _dragStartColumn(e: any, col: ICol): void {
		const target = e.target as HTMLElement;
		const testCustom = (el: HTMLElement) => el.classList.contains("dhx_grid-custom-content-cell");
		const isCustom = testCustom(target.parentElement) || testCustom(target.parentElement.parentElement);
		if (
			!isCustom &&
			(col.draggable ||
				(this.config.dragItem === "column" && col.draggable !== false) ||
				(this.config.dragItem === "both" && col.draggable !== false)) &&
			!locateNodeByClassName(e, "dhx_resizer_grip_wrap")
		) {
			if (e.targetTouches) {
				this._touch.start = true;
			}
			dragManager.onMouseDown(e, [col.id], [this._getColumnGhost(col)]);
		}
	}
}
